import {
  DraftPropertyListDto,
  EtxeApiPrefixes,
  EtxeApiSlugs,
  FileId,
  GouvGeorisquesDto,
  InvitePropertyMemberDto,
  ListPagination,
  PropertyCharacteristicsDto,
  PropertyConstructionDto,
  PropertyDocumentType,
  PropertyDpeDto,
  PropertyDto,
  PropertyFileDto,
  PropertyFinancialsDto,
  PropertyId,
  PropertyKeyInfoDto,
  PropertyLocationDto,
  PropertyMandateDto,
  PropertyMediaDto,
  PropertyPresentationDto,
  UserId,
} from '@workspace/data-types';
import { Observable } from 'rxjs';
import { ApiCaller } from '../../../../../api-caller.service';
import { ApiEntityStateConnector } from '../../../../../api-entity-state-connector.abstract-service';
import { QueryParamsUtils } from '../../../../../utils';

export class PropertiesOwnerConnector extends ApiEntityStateConnector<PropertyDto, PropertyDto, PropertyId> {
  constructor(apiCaller: ApiCaller, apiPrefix: string, parentSlug: string) {
    super(apiCaller, apiPrefix, `${parentSlug}/${EtxeApiSlugs.OWNER}`);
  }

  summerized = () => ({
    list: (pagination?: ListPagination): Observable<PropertyDto[]> =>
      this.apiCaller.getRequest<PropertyDto[]>(
        EtxeApiPrefixes.CORE,
        `${this.slug}/all${QueryParamsUtils.buildFilterParams(pagination)}`,
      ),
    count: (pagination?: ListPagination): Observable<number> =>
      this.apiCaller.getRequest<number>(
        EtxeApiPrefixes.CORE,
        `${this.slug}/all/count${QueryParamsUtils.buildFilterParams(pagination, true)}`,
      ),
  });

  drafts = (): Observable<DraftPropertyListDto[]> =>
    this.apiCaller.getRequest<DraftPropertyListDto[]>(EtxeApiPrefixes.CORE, `${this.slug}/drafts`);

  finish(propertyId: PropertyId): Observable<PropertyDto> {
    return this.apiCaller.patchRequest<PropertyDto>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/${propertyId}/finish`,
      {},
    );
  }

  previewAsAgent(propertyId: PropertyId): Observable<PropertyDto> {
    return this.apiCaller.getRequest<PropertyDto>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/${propertyId}/preview-as-agent`,
    );
  }

  uploadPhoto(propertyId: PropertyId, file: File): Observable<PropertyFileDto | undefined> {
    return this.apiCaller.multipartRequest(
      EtxeApiPrefixes.CORE,
      `${this.slug}/${propertyId}/photo`,
      'post',
      {},
      file,
    );
  }

  uploadDocument(
    propertyId: PropertyId,
    file: File,
    type: PropertyDocumentType = PropertyDocumentType.OWNERSHIP,
    label?: string,
  ): Observable<PropertyDto> {
    return this.apiCaller.multipartRequest<Record<string, unknown>, PropertyDto>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/${propertyId}/document`,
      'post',
      { type, label },
      file,
    );
  }

  deleteDocument(propertyId: PropertyId, fileId: FileId): Observable<PropertyDto> {
    return this.apiCaller.deleteRequest<PropertyDto>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/${propertyId}/document/${fileId}`,
    );
  }

  patch = (propertyId: PropertyId) => ({
    location: (locationDto: PropertyLocationDto): Observable<PropertyLocationDto> =>
      this.apiCaller.patchRequest(EtxeApiPrefixes.CORE, `${this.slug}/${propertyId}/location`, locationDto),
    keyInfo: (keyInfoDto: PropertyKeyInfoDto): Observable<PropertyKeyInfoDto> =>
      this.apiCaller.patchRequest(EtxeApiPrefixes.CORE, `${this.slug}/${propertyId}/key-info`, keyInfoDto),
    characteristics: (
      characteristicsDto: PropertyCharacteristicsDto,
    ): Observable<PropertyCharacteristicsDto> =>
      this.apiCaller.patchRequest(
        EtxeApiPrefixes.CORE,
        `${this.slug}/${propertyId}/characteristics`,
        characteristicsDto,
      ),
    construction: (constructionDto: PropertyConstructionDto): Observable<PropertyConstructionDto> =>
      this.apiCaller.patchRequest(
        EtxeApiPrefixes.CORE,
        `${this.slug}/${propertyId}/construction`,
        constructionDto,
      ),
    dpe: (dpeDto: PropertyDpeDto): Observable<PropertyDpeDto> =>
      this.apiCaller.patchRequest(EtxeApiPrefixes.CORE, `${this.slug}/${propertyId}/dpe`, dpeDto),
    geohazards: (): Observable<GouvGeorisquesDto> =>
      this.apiCaller.patchRequest(EtxeApiPrefixes.CORE, `${this.slug}/${propertyId}/geohazards`),
    presentation: (presentationDto: PropertyPresentationDto): Observable<PropertyPresentationDto> =>
      this.apiCaller.patchRequest(
        EtxeApiPrefixes.CORE,
        `${this.slug}/${propertyId}/presentation`,
        presentationDto,
      ),
    media: (mediaDto: PropertyMediaDto): Observable<PropertyMediaDto> =>
      this.apiCaller.patchRequest(EtxeApiPrefixes.CORE, `${this.slug}/${propertyId}/media`, mediaDto),
    financials: (financialsDto: PropertyFinancialsDto): Observable<PropertyFinancialsDto> =>
      this.apiCaller.patchRequest(
        EtxeApiPrefixes.CORE,
        `${this.slug}/${propertyId}/financials`,
        financialsDto,
      ),
    mandate: (mandateDto: PropertyMandateDto): Observable<PropertyMandateDto> =>
      this.apiCaller.patchRequest(EtxeApiPrefixes.CORE, `${this.slug}/${propertyId}/mandate`, mandateDto),
  });

  members = (propertyId: PropertyId) => ({
    invite: (dto: InvitePropertyMemberDto): Observable<PropertyDto> =>
      this.apiCaller.postRequest<PropertyDto, InvitePropertyMemberDto>(
        EtxeApiPrefixes.CORE,
        `${this.slug}/${propertyId}/members/invite`,
        dto,
      ),
    resend: (memberIdentifier: string | UserId): Observable<PropertyDto> =>
      this.apiCaller.postRequest<PropertyDto, void>(
        EtxeApiPrefixes.CORE,
        `${this.slug}/${propertyId}/members/${encodeURIComponent(String(memberIdentifier))}/resend`,
      ),
    remove: (memberIdentifier: string | UserId): Observable<PropertyDto> =>
      this.apiCaller.deleteRequest<PropertyDto>(
        EtxeApiPrefixes.CORE,
        `${this.slug}/${propertyId}/members/${encodeURIComponent(String(memberIdentifier))}`,
      ),
  });

  acceptMemberInvitation(token: string): Observable<PropertyDto> {
    return this.apiCaller.postRequest<PropertyDto, { token: string }>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/members/accept-invitation`,
      { token },
    );
  }

  declineMemberInvitation(token: string): Observable<PropertyDto> {
    return this.apiCaller.postRequest<PropertyDto, { token: string }>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/members/decline-invitation`,
      { token },
    );
  }

  getPendingInvitations(): Observable<PropertyDto[]> {
    return this.apiCaller.getRequest<PropertyDto[]>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/members/pending-invitations`,
    );
  }

  acceptInvitationByPropertyId(propertyId: PropertyId): Observable<PropertyDto> {
    return this.apiCaller.postRequest<PropertyDto, void>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/${propertyId}/members/accept`,
    );
  }

  declineInvitationByPropertyId(propertyId: PropertyId): Observable<PropertyDto> {
    return this.apiCaller.postRequest<PropertyDto, void>(
      EtxeApiPrefixes.CORE,
      `${this.slug}/${propertyId}/members/decline`,
    );
  }
}
